import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { adminService } from '@/api/services/adminService';
import * as Icons from '@tabler/icons-react';
import { RootState } from "@/redux/reducer/reducer";

export interface NavLink {
  title: string;
  label?: string;
  href: string;
  icon: JSX.Element | null;
  sub?: NavLink[];
}

export interface SideLink extends NavLink {}

/**
 * Dynamically maps a database item to a SideLink.
 * The icon is dynamically determined by the icon name saved in the DB.
 */
function mapItem(item: any): SideLink {
  // Trim any extra spaces from the icon string.
  const iconStr = item.icon ? item.icon.trim() : '';
  // Dynamically look up the icon component from the imported Icons object.
  const IconComponent = iconStr && (Icons as any)[iconStr] ? (Icons as any)[iconStr] : null;
  
  // Safely handle routerLink. If null, default to an empty string.
  const routerLink = item.routerLink?.trim() ?? '';

  // Determine the href value without using nested ternary operators.
  let hrefValue = '';
  if (routerLink && routerLink !== '#') {
    hrefValue = routerLink.startsWith('/') ? routerLink : `/${routerLink}`;
  }

  const mappedItem: SideLink = {
    title: item.functionalName,
    label: '', // Optionally, you can use item.routerDescription if needed.
    href: hrefValue || '#', // If no valid link, set it as '#' but we handle its behavior separately.
    icon: IconComponent ? <IconComponent size={18} /> : null,
    sub: [],
  };

  console.log('Mapped item:', mappedItem);
  return mappedItem;
}

/**
 * Builds a tree structure from the flat list of items.
 * If an item has a parentId, it is nested under its parent; otherwise, it becomes a root.
 */
function buildTree(items: any[]): SideLink[] {
  const itemMap: { [key: string]: SideLink } = {};
  const roots: SideLink[] = [];

  // Map each API item to our SideLink structure and store it by its id.
  items.forEach((item) => {
    const mapped = mapItem(item);
    itemMap[item.id] = mapped;
  });

  // Build tree: nest items that have a parent.
  items.forEach((item) => {
    if (item.parentId && itemMap[item.parentId]) {
      itemMap[item.parentId].sub = itemMap[item.parentId].sub || [];
      itemMap[item.parentId].sub!.push(itemMap[item.id]);
    } else {
      roots.push(itemMap[item.id]);
    }
  });

  return roots;
}

/**
 * Custom hook that fetches side links based on the user's roles.
 * It dynamically maps icon names to their corresponding icon components.
 */
export function useSideLinks() {
  const user = useSelector((state: RootState) => state.auth.user);
  const [sideLinks, setSideLinks] = useState<SideLink[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    const currentUser = user;
    
    async function fetchData() {
      try {
        const RoleIds = currentUser.roles;
        const response = await adminService.functionalAllNew({ RoleIds });
        const tree = buildTree(response.data);
        
        console.log('Side links tree:', tree);
        
        setSideLinks(tree);
      } catch (err: any) {
        setError(err.message ?? "Error fetching side links");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [user]);

  return { sideLinks, loading, error };
}

